import { Rule } from 'antd/lib/form';
import FormSelect from 'components/Select/FormSelect';
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { IItemForGetShortInfo, IGetCustomersShort, EntityStatus } from 'types';
import { useFetchData } from 'hooks/useFetchData';
import { SHOW_ARCHIVED_CUSTOMERS } from 'appConstants';
import { useTranslation } from 'react-i18next';
import { getCustomersShortInfo } from 'backend/customers';
import { TCreateTeaserSelectedItem } from '../../../../../../createTeaserState/createTeaserContext';
import { ISelectOption } from 'components/Select/types';
import { DropdownRender } from 'components/Select/DropdownRender';
import { ShowArchived } from 'components/Select/DropdownRender/ShowArchived';
import { ArchivedLabel } from 'components/Select/ArchivedLabel';

interface ICustomerSelect {
	setCustomer: (value: TCreateTeaserSelectedItem) => void;
	setFetchedCustomers: (value: IItemForGetShortInfo[]) => void;
	selectedCustomer: TCreateTeaserSelectedItem;
}

const CustomerSelect: React.FC<ICustomerSelect> = ({ setCustomer, setFetchedCustomers, selectedCustomer }) => {
	const { response, loading, refetch, responseError } = useFetchData<IGetCustomersShort, null>(getCustomersShortInfo);
	const [showArchived, setShowArchived] = useState<boolean>(localStorage.getItem(SHOW_ARCHIVED_CUSTOMERS) === 'true');
	const { t } = useTranslation('error');

	useEffect(() => {
		response && setFetchedCustomers(response.data.customers);
	}, [response]);

	const validationRules = useMemo<Rule[]>(() => [{ required: true, message: t('selectCustomer') }], [t]);

	const options = useMemo<ISelectOption[]>(() => {
		const items = response?.data.customers || [];
		const visibleItems = showArchived
			? items
			: items.filter((customer) => customer.status === EntityStatus.Active || customer.id === selectedCustomer);

		return visibleItems.map((customer) => ({
			...customer,
			additionalContent: customer.status === EntityStatus.Archived ? <ArchivedLabel /> : undefined,
		}));
	}, [response, showArchived, selectedCustomer]);

	const toggleShowArchived = useCallback(() => {
		localStorage.setItem(SHOW_ARCHIVED_CUSTOMERS, JSON.stringify(!showArchived));
		setShowArchived((prevValue) => !prevValue);
	}, [showArchived]);

	const renderSelectMenu = useCallback(
		(menu: React.ReactElement) => (
			<DropdownRender menu={menu}>
				<ShowArchived checked={showArchived} onChange={toggleShowArchived} />
			</DropdownRender>
		),
		[showArchived, toggleShowArchived],
	);

	return (
		<FormSelect
			testId="CustomerSelect"
			name="selectedCustomer"
			label="customerLabel"
			options={options}
			fetchError={responseError}
			loading={loading}
			onRefetch={refetch}
			onChange={setCustomer}
			validationRules={validationRules}
			keepSearchWithDropdownRender={true}
			dropdownRender={renderSelectMenu}
		/>
	);
};

export default React.memo(CustomerSelect);
